interface CookieOptions {
  expires?: Date | number; // Date object or days from now
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  httpOnly?: boolean;
}

interface CookieInfo {
  name: string;
  value: string;
  category: 'essential' | 'analytics' | 'marketing' | 'functional';
  purpose: string;
  expires?: Date;
}

class CookieService {
  private cookieRegistry: Map<string, CookieInfo> = new Map();
  private consentStatus: Record<string, boolean> = {
    essential: true,
    analytics: false,
    marketing: false,
    functional: false
  };

  /**
   * Set a cookie with proper options and registration
   */
  public setCookie(
    name: string, 
    value: string, 
    options: CookieOptions = {},
    category: 'essential' | 'analytics' | 'marketing' | 'functional' = 'essential',
    purpose: string = 'Application functionality'
  ): void {
    // Check consent before setting non-essential cookies
    if (category !== 'essential' && !this.consentStatus[category]) {
      console.warn(`[Cookie] Blocked ${category} cookie due to no consent: ${name}`);
      return;
    }

    // Register cookie info
    this.registerCookie(name, value, category, purpose, options.expires);

    // Build cookie string
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // Add expiration
    if (options.expires) {
      const expiryDate = typeof options.expires === 'number' 
        ? new Date(Date.now() + options.expires * 24 * 60 * 60 * 1000)
        : options.expires;
      cookieString += `; expires=${expiryDate.toUTCString()}`;
    }

    // Add path
    cookieString += `; path=${options.path || '/'}`;

    // Add domain
    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    // Add secure flag
    if (options.secure || window.location.protocol === 'https:') {
      cookieString += '; secure';
    }

    // Add SameSite
    cookieString += `; SameSite=${options.sameSite || 'Strict'}`;

    // Set the cookie
    document.cookie = cookieString;

    console.log(`[Cookie] Set ${category} cookie: ${name}`);
  }

  /**
   * Get a cookie value
   */
  public getCookie(name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  /**
   * Delete a cookie
   */
  public deleteCookie(name: string, path: string = '/', domain?: string): void {
    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    
    if (domain) {
      cookieString += `; domain=${domain}`;
    }
    
    document.cookie = cookieString;
    this.cookieRegistry.delete(name);
    
    console.log(`[Cookie] Deleted cookie: ${name}`);
  }

  /**
   * Register cookie information for compliance
   */
  private registerCookie(
    name: string, 
    value: string, 
    category: 'essential' | 'analytics' | 'marketing' | 'functional',
    purpose: string,
    expires?: Date | number
  ): void {
    const expiryDate = typeof expires === 'number' 
      ? new Date(Date.now() + expires * 24 * 60 * 60 * 1000)
      : expires;

    this.cookieRegistry.set(name, {
      name,
      value,
      category,
      purpose,
      expires: expiryDate
    });
  }

  /**
   * Get all registered cookies by category
   */
  public getCookiesByCategory(category?: 'essential' | 'analytics' | 'marketing' | 'functional'): CookieInfo[] {
    const cookies = Array.from(this.cookieRegistry.values());
    return category ? cookies.filter(c => c.category === category) : cookies;
  }

  /**
   * Clear all non-essential cookies
   */
  public clearNonEssentialCookies(): void {
    const nonEssentialCookies = this.getCookiesByCategory().filter(c => c.category !== 'essential');
    
    nonEssentialCookies.forEach(cookie => {
      this.deleteCookie(cookie.name);
    });

    console.log(`[Cookie] Cleared ${nonEssentialCookies.length} non-essential cookies`);
  }

  /**
   * Clear cookies by category
   */
  public clearCookiesByCategory(category: 'analytics' | 'marketing' | 'functional'): void {
    const cookies = this.getCookiesByCategory(category);
    
    cookies.forEach(cookie => {
      this.deleteCookie(cookie.name);
    });

    console.log(`[Cookie] Cleared ${cookies.length} ${category} cookies`);
  }

  /**
   * Check if cookies are enabled in browser
   */
  public areCookiesEnabled(): boolean {
    try {
      document.cookie = 'test_cookie=1; path=/';
      const enabled = document.cookie.indexOf('test_cookie=1') !== -1;
      this.deleteCookie('test_cookie');
      return enabled;
    } catch {
      return false;
    }
  }

  /**
   * Get cookie consent status
   */
  public getConsentStatus(): Record<string, boolean> {
    // Try to load from stored consent
    const stored = this.getCookie('mozo_cookie_consent');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        this.consentStatus = { ...this.consentStatus, ...parsed };
      } catch (error) {
        console.warn('[Cookie] Failed to parse stored consent');
      }
    }
    return this.consentStatus;
  }

  /**
   * Set consent status for cookie categories
   */
  public setConsentStatus(consent: Record<string, boolean>): void {
    this.consentStatus = { ...consent };
    
    // Store consent in cookie (essential category)
    this.setCookie(
      'mozo_cookie_consent',
      JSON.stringify(consent),
      { expires: 365 }, // 1 year
      'essential',
      'Stores user cookie consent preferences'
    );
    
    // Clear non-consented cookies
    this.clearNonConsentedCookies();
    
    console.log('[Cookie] Consent updated:', this.consentStatus);
  }

  /**
   * Clear cookies that don't have consent
   */
  private clearNonConsentedCookies(): void {
    this.cookieRegistry.forEach((info, name) => {
      if (info.category !== 'essential' && !this.consentStatus[info.category]) {
        this.deleteCookie(name);
        console.log(`[Cookie] Cleared non-consented ${info.category} cookie: ${name}`);
      }
    });
  }

  /**
   * Set essential authentication cookies
   */
  public setAuthCookies(email: string, rememberMe: boolean = false): void {
    if (rememberMe) {
      this.setCookie(
        'mozo_remember_email',
        email,
        { expires: 30 }, // 30 days
        'essential',
        'Remembers user email for login convenience'
      );
    }

    this.setCookie(
      'mozo_last_login',
      new Date().toISOString(),
      { expires: 7 }, // 7 days
      'essential',
      'Tracks last login time for security'
    );
  }

  /**
   * Set analytics cookies (only if consent given)
   */
  public setAnalyticsCookies(sessionId: string, userId?: string): void {
    const consent = this.getConsentStatus();
    
    if (!consent.analytics) {
      console.log('[Cookie] Analytics cookies blocked - no consent');
      return;
    }

    this.setCookie(
      'mozo_session_id',
      sessionId,
      { expires: 1 }, // 1 day
      'analytics',
      'Tracks user session for analytics'
    );

    if (userId) {
      this.setCookie(
        'mozo_user_id',
        userId,
        { expires: 30 }, // 30 days
        'analytics',
        'Associates analytics with user account'
      );
    }

    this.setCookie(
      'mozo_visit_count',
      String(parseInt(this.getCookie('mozo_visit_count') || '0') + 1),
      { expires: 365 }, // 1 year
      'analytics',
      'Counts total user visits'
    );
  }

  /**
   * Set functional cookies (only if consent given)
   */
  public setFunctionalCookies(preferences: Record<string, any>): void {
    const consent = this.getConsentStatus();
    
    if (!consent.functional) {
      console.log('[Cookie] Functional cookies blocked - no consent');
      return;
    }

    this.setCookie(
      'mozo_preferences',
      JSON.stringify(preferences),
      { expires: 90 }, // 90 days
      'functional',
      'Stores user preferences and settings'
    );
  }

  /**
   * Get comprehensive cookie report for compliance
   */
  public getCookieReport(): {
    total: number;
    byCategory: Record<string, number>;
    details: CookieInfo[];
    consentStatus: any;
  } {
    const cookies = this.getCookiesByCategory();
    const byCategory = {
      essential: cookies.filter(c => c.category === 'essential').length,
      analytics: cookies.filter(c => c.category === 'analytics').length,
      marketing: cookies.filter(c => c.category === 'marketing').length,
      functional: cookies.filter(c => c.category === 'functional').length
    };

    return {
      total: cookies.length,
      byCategory,
      details: cookies,
      consentStatus: this.getConsentStatus()
    };
  }

  /**
   * Initialize cookie service with GDPR compliance
   */
  public initialize(): void {
    // Check if we have existing consent
    const hasConsent = this.getCookie('mozo_cookie_consent');
    
    if (!hasConsent) {
      // Clear all non-essential cookies on first visit
      this.clearNonEssentialCookies();
      console.log('[Cookie] First visit - cleared non-essential cookies');
    }

    // Log current cookie status
    const report = this.getCookieReport();
    console.log('[Cookie] Service initialized:', report);
  }
}

// Create singleton instance
export const cookieService = new CookieService();

// React hook for cookie management
export function useCookies() {
  return {
    setCookie: (name: string, value: string, options?: CookieOptions, category?: 'essential' | 'analytics' | 'marketing' | 'functional', purpose?: string) =>
      cookieService.setCookie(name, value, options, category, purpose),
    getCookie: (name: string) => cookieService.getCookie(name),
    deleteCookie: (name: string, path?: string, domain?: string) => cookieService.deleteCookie(name, path, domain),
    getConsentStatus: () => cookieService.getConsentStatus(),
    setConsentStatus: (consent: any) => cookieService.setConsentStatus(consent),
    getCookieReport: () => cookieService.getCookieReport(),
    areCookiesEnabled: () => cookieService.areCookiesEnabled()
  };
}

export default cookieService;
